<script lang="ts" setup>
import {ArrowUpRightIcon} from "@heroicons/vue/16/solid";
import LoadingIcon from "~/components/icon/LoadingIcon.vue";
import {menuMainItems} from "~~/route/route.config";
import {useI18nCollection} from "~/composables/useI18nCollection";
import {EnvelopeOpenIcon, MapPinIcon, PhoneIcon} from "@heroicons/vue/20/solid";
// Types pour l'API reCAPTCHA
declare global {
    interface Window {
        grecaptcha?: {
            render: (container: Element, parameters: { sitekey: string }) => number;
            reset: (widgetId?: number) => void;
            getResponse: (widgetId?: number) => string;
        };
        onRecaptchaLoad?: () => void;
    }
}
const {collectionUri} = useI18nCollection();
const {pageUri} = useI18nCollection();
const {t} = useI18n();
const index = await queryCollection(collectionUri('contact')).first();
// SEO optimization
useSeo({
    title: index?.seoTitle || t('seo.contact.title'),
    description: index?.seoDescription || t('seo.contact.description'),
});
const subjects: string[] = ['general', 'quote', 'support', 'other'];
const selectedSubject = ref<string>(subjects[0] as string);
const serverError = ref<string>('');
const imgUri = computed<string>(() => (index?.meta?.imageRef as string || '').replace('/assets', ''));
const input = ref({
    firstname: '',
    lastname: '',
    email: '',
    subject: selectedSubject.value,
    postal_code: '',
    message: '',
    phone: '',
    consent: false,
});
const inputError = ref({
    firstname: false,
    lastname: false,
    email: false,
    postal_code: false,
    message: false,
    consent: false,
    captcha: false,
    phone: false,
});
const emailRegex = new RegExp('^(([^<>()[\\]\\\\.,;:\\s@\\"]+(\\.[^<>()[\\]\\\\.,;:\\s@\\"]+)*)|(\\".+\\"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$', 'gm')
const recaptchaContainer = ref<HTMLElement>();
const config = useRuntimeConfig().public;
const contactInfo = config.contact;
const loading = ref(false);
const disableForm = ref(false);
const isSuccess = ref(false);
const {locale} = useI18n();
const privacyPolicyUri = computed(() =>
        menuMainItems.find(item => item.identifier === 'privacy-policy')!.path[locale.value]
);
const hasInputError = computed(() => Object.values(inputError.value).some((error) => error));

function onSubmit() {
    loading.value = true;
    inputError.value.firstname = input.value.firstname.trim() === '';
    inputError.value.lastname = input.value.lastname.trim() === '';
    inputError.value.email = !emailRegex.test(input.value.email.toLowerCase()) || input.value.email === '';
    inputError.value.phone = input.value.phone.trim() === '';
    inputError.value.postal_code = input.value.postal_code.trim() === '';
    inputError.value.message = input.value.message.trim() === '';
    inputError.value.consent = !input.value.consent;
    if (Object.values(inputError.value).some((error) => error)) {
        loading.value = false;
        return;
    } else {
        input.value.subject = selectedSubject.value;
    }
    const captchaToken = recaptchaContainer.value?.querySelector<HTMLTextAreaElement>('#g-recaptcha-response')?.value || '';
    if (!captchaToken) {
        inputError.value.captcha = true;
        loading.value = false;
        return;
    }
    fetch(config.contactFormAction, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
            ...Object.keys(input.value).reduce((acc: Record<string, string>, key: string) => {
                acc[key] = input.value[key as keyof typeof input.value].toString();
                return acc;
            }, {}),
            g_recaptcha_response: captchaToken,
        })
    })
            .then(response => response.json())
            .then((response: { success?: boolean, message: string }) => {
                if (response?.success) {
                    disableForm.value = true;
                    loading.value = false;
                    isSuccess.value = true;
                } else {
                    handleError(response.message);
                }
            }).catch(error => {
        handleError(error);
    }).finally(() => loading.value = false);
}

function handleError(error: string) {
    serverError.value = error;
    isSuccess.value = false;
}

let recaptchaWidgetId: number | null = null;
const renderRecaptcha = () => {
    if (typeof window === 'undefined' || !window.grecaptcha) return;
    // Réinitialiser le widget s'il existe déjà
    if (recaptchaWidgetId !== null) {
        try {
            window.grecaptcha.reset(recaptchaWidgetId);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_err) {
            // Si reset échoue, on va re-render
            recaptchaWidgetId = null;
        }
    }
    // Rendre le widget si nécessaire
    if (recaptchaWidgetId === null && recaptchaContainer.value) {
        const recaptchaElement = recaptchaContainer.value.querySelector('.g-recaptcha');
        if (recaptchaElement && !recaptchaElement.hasChildNodes()) {
            try {
                recaptchaWidgetId = window.grecaptcha.render(recaptchaElement, {
                    sitekey: config.recaptchaSiteKey
                });
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (_err) {
                console.error('Erreur lors du rendu du reCAPTCHA');
            }
        }
    }
};
const loadScript = () => {
    const existingScript = document.getElementById('recaptcha-script');
    if (existingScript && window.grecaptcha?.render) {
        renderRecaptcha();
        return;
    }
    if (existingScript) {
        existingScript.remove();
    }
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit&hl=' + locale.value;
    script.id = 'recaptcha-script';
    script.async = true;
    script.defer = true;
    window.onRecaptchaLoad = () => {
        renderRecaptcha();
    };
    document.body.appendChild(script);
};
onMounted(() => {
    loadScript();
});
onUnmounted(() => {
    // Nettoyer le widget lors du démontage
    if (recaptchaWidgetId !== null && window.grecaptcha?.reset) {
        try {
            window.grecaptcha.reset(recaptchaWidgetId);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_err) {
        }
    }
});
watch(locale, () => {
    const existingScript = document.getElementById('recaptcha-script');
    if (existingScript) {
        existingScript.remove();
    }
    recaptchaWidgetId = null;
    nextTick(() => {
        loadScript();
    });
});

</script>

<template>
  <main class="tablet:flex page-container tablet:gap-x-12 desktop:gap-x-20 wide:gap-x-26">
    <article class="basis-1/2 shrink-0">
      <header class="mb-8">
        <h1 class="section-title text-secondary">
          {{ index.title }}
        </h1>
      </header>
      <ContentRenderer :value="index" class="with-free-text mb-8 tablet:hidden"/>
      <div class="mb-8 tablet:hidden">
        <ul class="grid grid-cols-3">
          <li v-for="[key, uri] of Object.entries(contactInfo)" :key="key" class="text-center">
            <div class="mb-4">
              <EnvelopeOpenIcon v-if="key === 'email'" class="size-6 text-brand-dark inline-block"/>
              <PhoneIcon v-if="key === 'phone'" class="size-6 text-brand-dark inline-block"/>
              <MapPinIcon v-if="key === 'address'" class="size-6 text-brand-dark inline-block"/>
            </div>
            <div class="text-center text-sm">
              <a v-if="key === 'email'" :href="`mailto:${uri}`" class="text-dark hover:text-brand-dark">
                {{ uri }}
              </a>
              <a v-else-if="key === 'phone'" :href="`tel:${uri}`" class="text-dark hover:text-brand-dark">
                {{ uri }}
              </a>
              <a
                      v-else-if="key === 'address'" :href="uri.url"
                      class="text-dark hover:text-brand-dark">
                {{ uri.human }}
              </a>
            </div>
          </li>
        </ul>
      </div>
      <form
              action="https://api.htmlandtiti.es/contact/enerwin" class="mb-12" method="post"
              @submit.prevent="onSubmit"
      >
        <div class="tablet:grid tablet:grid-cols-2 tablet:gap-4 mb-4">
          <div class="mb-4">
            <div class="rounded-md bg-white px-3 pt-2.5 pb-1.5 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-brand-dark">
              <label
                      class="block text-xs font-medium text-gray-900"
                      for="firstname">{{ t('contact.firstname.label') }} </label>
              <input
                      id="firstname"
                      v-model="input.firstname"
                      :disabled="disableForm"
                      class="block w-full text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6"
                      name="firstname"
                      required
                      type="text"
              >
            </div>
            <small v-if="inputError.firstname" class="text-error mt-1 block text-sm">
              {{ t('contact.firstname.error') }}
            </small>
          </div>
          <div class="mb-4">
            <div class="rounded-md bg-white px-3 pt-2.5 pb-1.5 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-brand-dark">
              <label
                      class="block text-xs font-medium text-gray-900"
                      for="lastname">{{ t('contact.lastname.label') }}</label>
              <input
                      id="lastname"
                      v-model="input.lastname"
                      :disabled="disableForm"
                      class="block w-full text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6"
                      name="name"
                      required
                      type="text"
              >
            </div>
            <small v-if="inputError.lastname" class="text-error mt-1 block text-sm">
              {{ t('contact.lastname.error') }}
            </small>
          </div>
        </div>
        <div class="mb-4">
          <div class="rounded-md bg-white px-3 pt-2.5 pb-1.5 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-brand-dark">
            <label
                    class="block text-xs font-medium text-gray-900"
                    for="phone">{{ t('contact.phone.label') }}</label>
            <input
                    id="phone"
                    v-model="input.phone"
                    :disabled="disableForm"
                    class="block w-full text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6"
                    name="phone"
                    required
                    type="tel"
            >
          </div>
          <small v-if="inputError.phone" class="text-error mt-1 block text-sm">
            {{ t('contact.phone.error') }}
          </small>
        </div>
        <div class="mb-6">
          <div class="rounded-md bg-white px-3 pt-2.5 pb-1.5 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-brand-dark">
            <label
                    class="block text-xs font-medium text-gray-900"
                    for="email">{{ t('contact.email.label') }}</label>
            <input
                    id="email"
                    v-model="input.email"
                    :disabled="disableForm"
                    class="block w-full text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6"
                    name="email"
                    required
                    type="email"
            >
          </div>
          <small v-if="inputError.email" class="text-error mt-1 block text-sm">
            {{ t('contact.email.error') }}
          </small>
        </div>
        <div class="mb-6">
          <div class="rounded-md bg-white px-3 pt-2.5 pb-1.5 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-brand-dark">
            <label
                    class="block text-xs font-medium text-gray-900"
                    for="postal_code">{{ t('contact.postal_code.label') }}</label>
            <input
                    id="postal_code"
                    v-model="input.postal_code"
                    :disabled="disableForm"
                    class="block w-full text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6"
                    name="postal_code"
                    required
                    type="text"
            >
          </div>
          <small v-if="inputError.postal_code" class="text-error mt-1 block text-sm">
            {{ t('contact.postal_code.error') }}
          </small>
        </div>
        <div class="flex flex-wrap gap-2 mb-6">
          <span class="basis-full shrink-0 text-sm">
            {{ t('contact.subject.label') }}
          </span>
          <button
                  v-for="subject in subjects"
                  :key="subject"
                  :class="{
                      'bg-brand/50 text-tertiary': selectedSubject !== subject,
                      'bg-brand-dark text-light': selectedSubject === subject
                  }"
                  class="btn-sm "
                  type="button"
                  @click="selectedSubject = subject"
          >
            {{ t('contact.subject.' + subject) }}
          </button>
        </div>
        <div>
          <label class="block text-sm/6 font-medium text-gray-900" for="message">
            {{ t('contact.message.label') }}
          </label>
          <div class="mt-2">
            <textarea
                    id="message"
                    v-model="input.message"
                    :disabled="disableForm"
                    class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-brand-dark sm:text-sm/6"
                    name="message"
                    required
                    rows="8"/>
          </div>
          <small v-if="inputError.message" class="text-error mt-1 block text-sm">
            {{ t('contact.message.error') }}
          </small>
        </div>
        <div class="mb-4">
          <div class="relative flex gap-3 pt-3.5">
            <div class="flex h-6 shrink-0 items-center">
              <div class="group grid size-4 grid-cols-1">
                <input
                        id="consent"
                        v-model="input.consent"
                        aria-describedby="terms"
                        class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-brand-dark checked:bg-brand-dark indeterminate:border-brand-dark indeterminate:bg-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-dark disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                        name="consent"
                        required
                        type="checkbox">
                <svg
                        class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                        fill="none" viewBox="0 0 14 14">
                  <path
                          class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-linecap="round"
                          stroke-linejoin="round" stroke-width="2"/>
                  <path
                          class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-linecap="round"
                          stroke-linejoin="round" stroke-width="2"/>
                </svg>
              </div>
            </div>
            <div class="min-w-0 flex-1 text-sm/6">
              <p id="terms" class="text-grey ">
                <i18n-t keypath="contact.consent.label">
                  <template #link>
                    <SiteHydrationSafeLink
                            :to="pageUri(privacyPolicyUri)" class="link"
                            rel="noopener" target="_blank"
                    >
                      {{ t('contact.consent.link_text') }}
                    </SiteHydrationSafeLink>
                  </template>
                </i18n-t>
              </p>
            </div>
          </div>
          <small v-if="inputError.consent" class="text-error mt-1 block text-sm">
            {{ t('contact.consent.error') }}
          </small>
        </div>
        <div ref="recaptchaContainer" class="mb-8">
          <div :data-sitekey="config.recaptchaSiteKey" class="g-recaptcha"/>
        </div>
        <transition name="slide-fade">
          <div v-if="serverError" class="mb-4">
            <div class="bg-error/10 border border-error/15 px-6 py-4 rounded">
              <p class="text-error">
                {{ t('contact.error', {email: config.contact.email}) }} <br>
                <strong class="text-error">{{ serverError }}</strong>
              </p>
            </div>
          </div>
        </transition>
        <transition name="slide-fade">
          <div v-if="isSuccess" class="mb-4">
            <div class="bg-success/10 border border-success/15 px-6 py-4 rounded">
              <p class="text-success">
                {{ t('contact.success') }}
              </p>
            </div>
          </div>
        </transition>
        <transition name="slide-fade">
          <div v-if="hasInputError" class="mb-4">
            <div class="bg-error/10 border border-error/15 px-6 py-4 rounded">
              <p class="text-error">
                {{ t('contact.validation_error') }}
              </p>
            </div>
          </div>
        </transition>
        <div class="mb-6">
          <button
                  :class="{'opacity-50 pointer-events-none': disableForm}"
                  :disabled="disableForm"
                  class="btn-brand inline-flex items-center" type="submit" @click.prevent="onSubmit">
            <LoadingIcon v-if="loading" class="me-3 size-4"/>
            {{ t('contact.submit') }}
            <ArrowUpRightIcon class="size-5 ms-2"/>
          </button>
        </div>
      </form>
    </article>
    <aside class="hidden tablet:block">
      <ContentRenderer :value="index" class="with-free-text mb-8"/>

      <div class="mb-8">
        <ul class="grid grid-cols-3">
          <li v-for="[key, uri] of Object.entries(contactInfo)" :key="key" class="text-center">
            <div class="mb-4">
              <EnvelopeOpenIcon v-if="key === 'email'" class="size-8 text-brand-dark inline-block"/>
              <PhoneIcon v-if="key === 'phone'" class="size-8 text-brand-dark inline-block"/>
              <MapPinIcon v-if="key === 'address'" class="size-8 text-brand-dark inline-block"/>
            </div>
            <div class="text-center">
              <a v-if="key === 'email'" :href="`mailto:${uri}`" class="text-dark hover:text-brand-dark">
                {{ uri }}
              </a>
              <a v-else-if="key === 'phone'" :href="`tel:${uri}`" class="text-dark hover:text-brand-dark">
                {{ uri }}
              </a>
              <a
                      v-else-if="key === 'address'" :href="uri.url"
                      class="text-dark hover:text-brand-dark">
                {{ uri.human }}
              </a>
            </div>
          </li>
        </ul>
      </div>
      <picture
              v-if="imgUri"
      >
        <media-source
                :dimensions="[
                        { width: 1920 },
                        { width: 1280 },
                        { width: 640 }
                      ]"
                :uri="imgUri"
                as="source"
                format="webp"
                sizes="100vw, (min-width: 768px) 33vw"
        />
        <media-source
                :alt="t('seo.images.contact')"
                :dimensions="[
                        { width: 1920 },
                        { width: 1280 },
                        { width: 640 }
                      ]"
                :uri="imgUri"
                as="img"
                class-name="rounded-img"
                format="jpg"
                sizes="(min-width: 768px) 33vw"
        />
      </picture>
    </aside>
  </main>
</template>
